import { tutorialAssets } from "assets/cdn";
import { useImagePath } from "hooks/useImagePath";
import { FC, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTutorialAnchor } from "../../../TutorialV2/hooks";
import { ButtonShineLayer } from "../../../TutorialV2/lib/Step3/styles";
import { TutorialV2StepProps } from "../../types";
import {
	Arrow,
	ArrowDownContainer,
	Container,
	Content,
	NextButton,
	Slide,
	SlideNumber,
	Tiki,
	Title
} from "../styles";
import { TutorialPanelScale } from "../Step1/styles";
import {
	LoyaltyButtonShine,
	RedeemShine,
	RedeemText,
	RedeemWord
} from "./styles";

const ARROW_CENTER_EM = 7.5;

const getFirstVisibleLoyaltyOffer = (): HTMLElement | null => {
	const offers = Array.from(
		document.querySelectorAll<HTMLElement>(
			"[data-tutorial-loyalty-single-offer]"
		)
	);

	return (
		offers.find((offer) => {
			const rect = offer.getBoundingClientRect();
			const style = window.getComputedStyle(offer);

			return (
				rect.width > 0 &&
				rect.height > 0 &&
				style.display !== "none" &&
				style.visibility !== "hidden"
			);
		}) ?? null
	);
};

export const Step2: FC<TutorialV2StepProps> = ({
	currentStep,
	totalSteps,
	onNext
}) => {
	const { getImage } = useImagePath("cdn");
	const background = getImage(tutorialAssets.background);
	const ArrowImage = getImage(tutorialAssets.greenArrow);
	const ShineImage = getImage(tutorialAssets.shine);
	const TikiImage = getImage(tutorialAssets.char2);
	const { targetElement, targetRect } = useTutorialAnchor({
		anchorSelector: "button",
		panelAnchorOffsetEm: 5.5,
		panelPlacement: "below",
		panelSelector: "[data-tutorial-lp-v2-step2-panel]",
		portraitScrollOffsetSelector: "#sub-menu.section-menu-container",
		scrollMode: "tutorial-offer-side",
		targetResolver: getFirstVisibleLoyaltyOffer
	});
	const offerElement = targetElement;
	const purchaseButton = targetElement?.querySelector<HTMLElement>("button");
	const purchaseButtonRect = targetRect;
	const offerRect = offerElement?.getBoundingClientRect();
	const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
	const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
	const isCompactLandscape =
		viewportWidth > viewportHeight && viewportHeight <= 500;
	const panelPlacement = isCompactLandscape ? "right" : "below";
	const panelAnchorLeft = isCompactLandscape
		? offerRect?.right
		: targetRect?.left;
	const panelAnchorTop = isCompactLandscape
		? offerRect
			? offerRect.top + offerRect.height / 2
			: undefined
		: targetRect?.bottom;
	const tutorialRoot = document.querySelector<HTMLElement>(
		"[data-tutorial-v2-root]"
	);
	const oneEm = tutorialRoot
		? parseFloat(window.getComputedStyle(tutorialRoot).fontSize)
		: 10;

	useEffect(() => {
		if (!offerElement) {
			return;
		}

		const previousPosition = offerElement.style.position;
		const previousZIndex = offerElement.style.zIndex;
		const previousButtonPosition = purchaseButton?.style.position ?? "";
		const previousButtonZIndex = purchaseButton?.style.zIndex ?? "";

		offerElement.style.position = offerElement.style.position || "relative";
		offerElement.style.zIndex = "10001";
		if (purchaseButton) {
			purchaseButton.style.position = "relative";
			purchaseButton.style.zIndex = "1";
		}

		return () => {
			offerElement.style.position = previousPosition;
			offerElement.style.zIndex = previousZIndex;
			if (purchaseButton) {
				purchaseButton.style.position = previousButtonPosition;
				purchaseButton.style.zIndex = previousButtonZIndex;
			}
			if (!offerElement.getAttribute("style")) {
				offerElement.removeAttribute("style");
			}
		};
	}, [offerElement, purchaseButton]);

	return (
		<>
			{offerElement &&
				purchaseButtonRect &&
				createPortal(
					<ButtonShineLayer>
						<LoyaltyButtonShine
							$left={purchaseButtonRect.left + purchaseButtonRect.width / 2}
							$top={purchaseButtonRect.top + purchaseButtonRect.height / 2}
							src={ShineImage}
						/>
					</ButtonShineLayer>,
					offerElement
				)}
			{createPortal(
				<TutorialPanelScale $fontSize={oneEm}>
					<Container
						$anchorCenterOffset={targetRect ? targetRect.width / 2 : 0}
						$anchorLeft={panelAnchorLeft}
						$anchorOffset={5.5}
						$anchorTop={panelAnchorTop}
						$arrowCenter={ARROW_CENTER_EM}
						$placement={panelPlacement}
						data-tutorial-lp-v2-step2-panel
					>
						<ArrowDownContainer
							className={isCompactLandscape ? "left" : undefined}
							$right={23.5}
						>
							<Arrow
								className={isCompactLandscape ? "left" : undefined}
								src={ArrowImage}
							/>
						</ArrowDownContainer>
						<SlideNumber>
							{currentStep}/{totalSteps}
						</SlideNumber>
						<Slide $backgroundImage={background}>
							<Tiki src={TikiImage} />
							<Content>
								<Title>
									<RedeemWord>
										<RedeemShine src={ShineImage} aria-hidden="true" />
										<RedeemText>Redeem</RedeemText>
									</RedeemWord>{" "}
									Loyalty Points
									<br /> for Volcanos, Wilds,
									<br /> Coins, and Boosters.
								</Title>
							</Content>
							<NextButton onClick={onNext}>Got It</NextButton>
						</Slide>
					</Container>
				</TutorialPanelScale>,
				document.body
			)}
		</>
	);
};
